import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import QuratorLayout from '../components/qurator/QuratorLayout';
import API from '../config/api';
import './HumanValidation.css';

const createDefaultValidationData = () => ({
  video_meta: { duration_hint_s: '', fps_hint: '' },
  objects: [],
  action_steps: [],
  timeline: [],
  reference_prompts: { global_context: '', per_step: [] }
});

const toNumberOrEmpty = (value) => {
  if (value === '' || value === null || typeof value === 'undefined') return '';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : '';
};

const normalizeStringArray = (items) => (
  Array.isArray(items) ? items.map((item) => (typeof item === 'string' ? item : String(item ?? ''))) : []
);

const isValidObjectName = (name) => typeof name === 'string' && name.trim().length >= 2;

const isValidOntologyObjectName = (name) => {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  if (!isValidObjectName(trimmed)) return false;
  if (trimmed.length > 80) return false;
  if (trimmed.split(/\s+/).length > 8) return false;
  if (/[.!?]/.test(trimmed)) return false;
  if (/^(user|person|hand fails|right hand|left hand|work surface|open position)$/i.test(trimmed)) {
    return false;
  }
  return true;
};

const sanitizeInventoryObjects = (objects) => (
  Array.isArray(objects)
    ? objects.filter((item) => isValidOntologyObjectName(item?.name)).map((item) => ({
      name: item.name.trim(),
      count: toNumberOrEmpty(item?.count) || 1
    }))
    : []
);

const normalizeInventoryForEdit = (objects) => (
  Array.isArray(objects)
    ? objects.map((item) => ({
      name: typeof item?.name === 'string' ? item.name : '',
      count: toNumberOrEmpty(item?.count) ?? 1,
      _key: item?._key || `obj-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    }))
    : []
);

const normalizeObjectsExpectedForEdit = (items) => normalizeStringArray(items);

const stripDraftRowsForSave = (data) => ({
  ...data,
  objects: sanitizeInventoryObjects(data.objects.map(({ _key, ...item }) => item)),
  action_steps: data.action_steps.map((step) => ({
    ...step,
    objects_expected: normalizeStringArray(step.objects_expected).filter(isValidOntologyObjectName)
  }))
});

const buildGlobalObjectRenames = (baselineObjects, currentObjects) => {
  const renames = [];
  const addRename = (from, to) => {
    if (!from || !to || namesMatch(from, to)) return;
    if (!renames.some((r) => namesMatch(r.from, from) && namesMatch(r.to, to))) {
      renames.push({ from: from.trim(), to: to.trim() });
    }
  };

  const baseline = Array.isArray(baselineObjects) ? baselineObjects : [];
  const current = Array.isArray(currentObjects) ? currentObjects : [];
  const maxLen = Math.max(baseline.length, current.length);

  for (let i = 0; i < maxLen; i += 1) {
    const fromName = baseline[i]?.name?.trim() || '';
    const toName = current[i]?.name?.trim() || '';
    if (fromName && toName && !namesMatch(fromName, toName)) {
      addRename(fromName, toName);
    }
  }

  return renames;
};

const summarizeActionStepChanges = (baseline, edited) => {
  if (!baseline || !edited) return [];
  const changes = [];
  if ((baseline.title || '') !== (edited.title || '')) {
    changes.push({ field: 'title', from: baseline.title || '', to: edited.title || '' });
  }
  if ((baseline.step_context || '') !== (edited.step_context || '')) {
    changes.push({ field: 'step_context', from: baseline.step_context || '', to: edited.step_context || '' });
  }
  const baseObjs = normalizeStringArray(baseline.objects_expected).filter(Boolean);
  const editObjs = normalizeStringArray(edited.objects_expected).filter(Boolean);
  if (JSON.stringify(baseObjs) !== JSON.stringify(editObjs)) {
    changes.push({ field: 'objects_expected', from: baseObjs, to: editObjs });
  }
  return changes;
};

const normalizeValidationData = (rawData) => {
  const data = rawData && typeof rawData === 'object' ? rawData : {};
  return {
    video_meta: {
      duration_hint_s: toNumberOrEmpty(data.video_meta?.duration_hint_s),
      fps_hint: toNumberOrEmpty(data.video_meta?.fps_hint)
    },
    objects: normalizeInventoryForEdit(data.objects),
    action_steps: Array.isArray(data.action_steps)
      ? data.action_steps.map((item, index) => ({
          id: toNumberOrEmpty(item?.id) || index + 1,
          title: typeof item?.title === 'string' ? item.title : '',
          step_context: typeof item?.step_context === 'string' ? item.step_context : '',
          detailed_steps: normalizeStringArray(item?.detailed_steps),
          objects_expected: normalizeObjectsExpectedForEdit(item?.objects_expected),
          hand_pose_expectation: {
            left: typeof item?.hand_pose_expectation?.left === 'string' ? item.hand_pose_expectation.left : '',
            right: typeof item?.hand_pose_expectation?.right === 'string' ? item.hand_pose_expectation.right : ''
          },
          success_criteria: normalizeStringArray(item?.success_criteria),
          failure_modes: normalizeStringArray(item?.failure_modes)
        }))
      : [],
    timeline: Array.isArray(data.timeline)
      ? data.timeline.map((item, index) => ({
          step_id: toNumberOrEmpty(item?.step_id) || index + 1,
          approx_start_s: toNumberOrEmpty(item?.approx_start_s),
          approx_end_s: toNumberOrEmpty(item?.approx_end_s)
        }))
      : [],
    reference_prompts: {
      global_context: typeof data.reference_prompts?.global_context === 'string'
        ? data.reference_prompts.global_context : '',
      per_step: Array.isArray(data.reference_prompts?.per_step)
        ? data.reference_prompts.per_step.map((item, index) => ({
            step_id: toNumberOrEmpty(item?.step_id) || index + 1,
            prompt: typeof item?.prompt === 'string' ? item.prompt : ''
          }))
        : []
    }
  };
};

const namesMatch = (a, b) => {
  const left = typeof a === 'string' ? a.trim().toLowerCase() : '';
  const right = typeof b === 'string' ? b.trim().toLowerCase() : '';
  return Boolean(left) && left === right;
};

const getOrderedSteps = (protocolData) => {
  if (Array.isArray(protocolData?.orderedSteps) && protocolData.orderedSteps.length > 0) {
    return protocolData.orderedSteps.filter((step) => step && step.trim());
  }
  if (typeof protocolData?.step_of_protocol === 'string') {
    return protocolData.step_of_protocol.split('`').filter((step) => step && step.trim());
  }
  return [];
};

const formatSeconds = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0:00';
  const mins = Math.floor(n / 60);
  const secs = Math.floor(n % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const getNextActionStepId = (actionSteps = []) => {
  const ids = actionSteps.map((s) => Number(s.id)).filter((id) => Number.isFinite(id));
  return ids.length ? Math.max(...ids) + 1 : 1;
};

const suggestNewStepTrim = (timeline = [], duration = 30) => {
  const sorted = [...timeline].sort((a, b) => Number(a.approx_start_s) - Number(b.approx_start_s));
  const dur = Number.isFinite(duration) && duration > 0 ? duration : 30;
  if (!sorted.length) return { start: 0, end: Math.min(5, dur) };

  let best = { start: 0, end: Math.min(3, dur), gap: 0 };
  sorted.forEach((entry, index) => {
    const gapStart = index === 0 ? 0 : Number(sorted[index - 1].approx_end_s) || 0;
    const gapEnd = Number(entry.approx_start_s) || 0;
    const gap = gapEnd - gapStart;
    if (gap > best.gap && gap >= 0.5) {
      best = { start: gapStart, end: gapEnd, gap };
    }
  });
  const lastEnd = Number(sorted[sorted.length - 1].approx_end_s) || 0;
  if (dur - lastEnd > best.gap && dur - lastEnd >= 0.5) {
    best = { start: lastEnd, end: Math.min(dur, lastEnd + 5), gap: dur - lastEnd };
  }
  if (best.gap < 0.5) {
    const mid = dur / 2;
    return { start: Math.max(0, mid - 2.5), end: Math.min(dur, mid + 2.5) };
  }
  return { start: best.start, end: best.end };
};

const mergeObjectsInventory = (fileObjects, editedObjects, actionStepObjects = []) => {
  const merged = (fileObjects || []).map((item) => ({
    name: typeof item?.name === 'string' ? item.name.trim() : '',
    count: Number(item?.count) || 1
  })).filter((item) => item.name);

  const namesLower = new Set(merged.map((item) => item.name.toLowerCase()));
  const addName = (rawName) => {
    const name = typeof rawName === 'string' ? rawName.trim() : '';
    if (!isValidObjectName(name) || namesLower.has(name.toLowerCase())) return;
    merged.push({ name, count: 1 });
    namesLower.add(name.toLowerCase());
  };

  (editedObjects || []).forEach((item) => addName(item?.name));
  (actionStepObjects || []).forEach((name) => addName(name));
  return merged;
};

const removeObjectFromGlobalOntology = (prev, objectName) => {
  const trimmed = objectName.trim();
  if (!isValidObjectName(trimmed)) return prev;

  return {
    ...prev,
    objects: prev.objects.filter((obj) => !namesMatch(obj.name, trimmed)),
    action_steps: prev.action_steps.map((step) => ({
      ...step,
      objects_expected: normalizeStringArray(step.objects_expected).filter(
        (item) => !namesMatch(item, trimmed)
      )
    }))
  };
};

const ListEditor = ({ label, items, onChange, onItemBlur, addLabel, placeholder }) => {
  const safeItems = items.length > 0 ? items : [''];
  return (
    <div className="validation-field-group">
      <div className="validation-field-head">
        <label className="form-label mb-0">{label}</label>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => onChange([...items, ''])}>
          <i className="bi bi-plus-lg me-2"></i>{addLabel}
        </button>
      </div>
      <div className="validation-list-stack">
        {safeItems.map((item, index) => (
          <div key={`${label}-${index}`} className="validation-list-row">
            <textarea
              className="form-control"
              rows="2"
              value={item}
              placeholder={placeholder}
              onChange={(event) => {
                const nextItems = [...safeItems];
                nextItems[index] = event.target.value;
                onChange(nextItems);
              }}
              onBlur={() => onItemBlur?.(index)}
            />
            <button
              type="button"
              className="btn btn-light validation-icon-btn"
              onClick={() => onChange(safeItems.filter((_, i) => i !== index))}
              disabled={safeItems.length === 1 && !safeItems[0]}
              aria-label={`Remove ${label}`}
            >
              <i className="bi bi-trash"></i>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const ActionStepObjectEditor = ({
  items,
  onChange,
  onItemFocus,
  onItemBlur,
  onRemoveItem,
  addLabel,
  placeholder
}) => {
  const safeItems = items.length > 0 ? items : [''];

  return (
    <div className="validation-field-group">
      <div className="validation-field-head">
        <label className="form-label mb-0">Expected Objects (this action)</label>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={() => {
            const current = items.length > 0 ? [...items] : [''];
            onChange([...current, '']);
          }}
        >
          <i className="bi bi-plus-lg me-2"></i>{addLabel}
        </button>
      </div>
      <p className="hitl-field-hint hv-object-sync-hint">
        Add objects you want the system to focus on. Reprocess keeps actual visible interactions from the clip and adds focus objects only when they are visible.
      </p>
      <div className="validation-list-stack">
        {safeItems.map((item, index) => (
          <div key={`action-object-${index}`} className="validation-list-row">
            <textarea
              className="form-control"
              rows="2"
              value={item}
              placeholder={placeholder}
              onFocus={() => onItemFocus(index)}
              onChange={(event) => {
                const nextItems = [...safeItems];
                nextItems[index] = event.target.value;
                onChange(nextItems);
              }}
              onBlur={() => onItemBlur(index)}
            />
            <button
              type="button"
              className="btn btn-light validation-icon-btn"
              onClick={() => onRemoveItem(index)}
              disabled={safeItems.length === 1 && !safeItems[0]}
              aria-label="Remove object from this action"
            >
              <i className="bi bi-trash"></i>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const DISCLAIMER_STORAGE_KEY = (protocolId, stepNumber) => `hv-disclaimer-v1-${protocolId}-${stepNumber}`;

const ValidationDisclaimerModal = ({ onAccept }) => (
  <div className="hv-disclaimer-overlay" role="dialog" aria-modal="true" aria-labelledby="hv-disclaimer-title">
    <div className="hv-disclaimer-modal">
      <div className="hv-disclaimer-icon"><i className="bi bi-shield-exclamation" /></div>
      <h2 id="hv-disclaimer-title">Human validation notice</h2>
      <p className="hv-disclaimer-lead">
        Changes you make here are applied to this protocol step and saved when you approve. They cannot be automatically reverted.
      </p>
      <ul className="hv-disclaimer-list">
        <li><i className="bi bi-check2" /> Edits to action steps, objects, and generated output persist after you approve.</li>
        <li><i className="bi bi-check2" /> Reprocess replaces visual reasoning for the current action step only.</li>
        <li><i className="bi bi-check2" /> Adjust clip timing on the timeline before reprocessing if the segment boundaries are wrong.</li>
        <li><i className="bi bi-check2" /> To undo, reload the page before approving — or restore from a previous backup in Edit Draft.</li>
      </ul>
      <button type="button" className="btn btn-primary hv-disclaimer-btn" onClick={onAccept}>
        I understand — continue to validation
      </button>
    </div>
  </div>
);

const UnifiedVideoTimeline = ({
  timeline,
  actionSteps,
  duration,
  activeStepId,
  currentTime,
  onSelectStep,
  onSeek,
  onTrimChange,
  newStepDraft = null,
  onNewStepTrimChange
}) => {
  const trackRef = useRef(null);
  const dragRef = useRef(null);
  const [hoveredStepId, setHoveredStepId] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ left: 0, visible: false, text: '' });

  const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : 1;
  const sorted = useMemo(
    () => [...timeline].sort((a, b) => Number(a.approx_start_s) - Number(b.approx_start_s)),
    [timeline]
  );

  const isDraftMode = Boolean(newStepDraft);
  const draftEntry = isDraftMode
    ? {
      step_id: newStepDraft.stepId,
      approx_start_s: Number(newStepDraft.approx_start_s) || 0,
      approx_end_s: Number(newStepDraft.approx_end_s) || safeDuration
    }
    : null;
  const activeEntry = isDraftMode
    ? draftEntry
    : sorted.find((e) => String(e.step_id) === String(activeStepId));
  const activeStart = Number(activeEntry?.approx_start_s) || 0;
  const activeEnd = Number(activeEntry?.approx_end_s) || safeDuration;

  const timeToPercent = (time) => Math.min(100, Math.max(0, (Number(time) / safeDuration) * 100));

  const getTimeFromClientX = useCallback((clientX) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return ratio * safeDuration;
  }, [safeDuration]);

  const getStepTitle = (stepId) => {
    if (isDraftMode && String(stepId) === String(newStepDraft.stepId)) {
      return newStepDraft.title?.trim() || 'New action step';
    }
    const step = actionSteps.find((s) => String(s.id) === String(stepId));
    return step?.title || `Action ${stepId}`;
  };

  const showTooltip = (entry, event) => {
    const start = Number(entry.approx_start_s) || 0;
    const end = Number(entry.approx_end_s) || start;
    const title = getStepTitle(entry.step_id);
    const rect = trackRef.current?.getBoundingClientRect();
    const left = rect ? event.clientX - rect.left : 0;
    setTooltipPos({
      left,
      visible: true,
      text: `${title}\n${formatSeconds(start)} – ${formatSeconds(end)}`
    });
    setHoveredStepId(entry.step_id);
  };

  const hideTooltip = () => {
    setTooltipPos((prev) => ({ ...prev, visible: false }));
    setHoveredStepId(null);
  };

  useEffect(() => {
    const onMove = (event) => {
      const drag = dragRef.current;
      if (!drag || !trackRef.current) return;

      const time = getTimeFromClientX(event.clientX);
      const stepId = drag.stepId;
      const entry = isDraftMode && String(stepId) === String(newStepDraft?.stepId)
        ? {
          step_id: newStepDraft.stepId,
          approx_start_s: Number(newStepDraft.approx_start_s) || 0,
          approx_end_s: Number(newStepDraft.approx_end_s) || safeDuration
        }
        : sorted.find((e) => String(e.step_id) === String(stepId));
      if (!entry) return;

      const currentStart = Number(entry.approx_start_s) || 0;
      const currentEnd = Number(entry.approx_end_s) || safeDuration;
      const minGap = 0.3;

      if (drag.type === 'playhead') {
        const clamped = Math.min(activeEnd - 0.05, Math.max(activeStart, time));
        onSeek(clamped);
        return;
      }

      if (drag.type === 'start') {
        const nextStart = Math.min(time, currentEnd - minGap);
        const patch = { approx_start_s: Math.max(0, nextStart) };
        if (isDraftMode && onNewStepTrimChange) onNewStepTrimChange(patch);
        else onTrimChange(stepId, patch);
        onSeek(Math.max(0, nextStart));
        return;
      }

      if (drag.type === 'end') {
        const nextEnd = Math.max(time, currentStart + minGap);
        const patch = { approx_end_s: Math.min(safeDuration, nextEnd) };
        if (isDraftMode && onNewStepTrimChange) onNewStepTrimChange(patch);
        else onTrimChange(stepId, patch);
      }
    };

    const onUp = () => {
      dragRef.current = null;
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [sorted, safeDuration, activeStart, activeEnd, getTimeFromClientX, onSeek, onTrimChange, isDraftMode, onNewStepTrimChange, newStepDraft]);

  const startDrag = (event, type, stepId) => {
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = { type, stepId };
  };

  const handleTrackClick = (event) => {
    if (dragRef.current) return;
    const time = getTimeFromClientX(event.clientX);
    const clicked = sorted.find((entry) => {
      const start = Number(entry.approx_start_s) || 0;
      const end = Number(entry.approx_end_s) || start;
      return time >= start && time <= end;
    });
    if (clicked) {
      onSelectStep(clicked.step_id);
      const start = Number(clicked.approx_start_s) || 0;
      const end = Number(clicked.approx_end_s) || start;
      onSeek(Math.min(end, Math.max(start, time)));
    }
  };

  return (
    <div className="hv-unified-timeline">
      <div
        className="hv-timeline-track"
        ref={trackRef}
        onClick={handleTrackClick}
        role="slider"
        aria-label="Video timeline"
        aria-valuemin={0}
        aria-valuemax={safeDuration}
        aria-valuenow={currentTime}
      >
        {sorted.map((entry) => {
          const start = Number(entry.approx_start_s) || 0;
          const end = Number(entry.approx_end_s) || start;
          const left = timeToPercent(start);
          const width = Math.max(timeToPercent(end) - left, 0.8);
          const isActive = !isDraftMode && String(entry.step_id) === String(activeStepId);
          const isHovered = String(entry.step_id) === String(hoveredStepId);

          return (
            <button
              key={entry.step_id}
              type="button"
              className={`hv-timeline-region ${isActive ? 'active' : ''} ${isHovered ? 'hovered' : ''}`}
              style={{ left: `${left}%`, width: `${width}%` }}
              onMouseEnter={(e) => showTooltip(entry, e)}
              onMouseMove={(e) => showTooltip(entry, e)}
              onMouseLeave={hideTooltip}
              onClick={(e) => {
                e.stopPropagation();
                if (isDraftMode) return;
                onSelectStep(entry.step_id);
                onSeek(start);
              }}
              aria-label={`${getStepTitle(entry.step_id)}, ${formatSeconds(start)} to ${formatSeconds(end)}`}
            />
          );
        })}

        {draftEntry && (
          <div
            className="hv-timeline-region hv-timeline-region-new active"
            style={{
              left: `${timeToPercent(Number(draftEntry.approx_start_s) || 0)}%`,
              width: `${Math.max(
                timeToPercent(Number(draftEntry.approx_end_s) || 0) - timeToPercent(Number(draftEntry.approx_start_s) || 0),
                0.8
              )}%`
            }}
            aria-hidden="true"
          />
        )}

        {activeEntry && (
          <>
            <div
              className="hv-timeline-active-ring"
              style={{
                left: `${timeToPercent(activeStart)}%`,
                width: `${Math.max(timeToPercent(activeEnd) - timeToPercent(activeStart), 0.5)}%`
              }}
            />
            <div
              className="hv-timeline-handle hv-timeline-handle-start"
              style={{ left: `${timeToPercent(activeStart)}%` }}
              onPointerDown={(e) => startDrag(e, 'start', activeEntry.step_id)}
              role="slider"
              aria-label="Trim clip start"
              aria-valuemin={0}
              aria-valuemax={safeDuration}
              aria-valuenow={activeStart}
              tabIndex={0}
            />
            <div
              className="hv-timeline-handle hv-timeline-handle-end"
              style={{ left: `${timeToPercent(activeEnd)}%` }}
              onPointerDown={(e) => startDrag(e, 'end', activeEntry.step_id)}
              role="slider"
              aria-label="Trim clip end"
              aria-valuemin={0}
              aria-valuemax={safeDuration}
              aria-valuenow={activeEnd}
              tabIndex={0}
            />
          </>
        )}

        <div
          className="hv-timeline-playhead"
          style={{ left: `${timeToPercent(currentTime)}%` }}
          onPointerDown={(e) => activeEntry && startDrag(e, 'playhead', activeEntry.step_id)}
          role="presentation"
        />
      </div>

      {tooltipPos.visible && (
        <div className="hv-timeline-tooltip" style={{ left: tooltipPos.left }}>
          {tooltipPos.text.split('\n').map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </div>
      )}

      <div className="hv-timeline-meta">
        <span><i className="bi bi-clock" /> {formatSeconds(currentTime)} / {formatSeconds(safeDuration)}</span>
        {activeEntry && (
          <span className="hv-timeline-segment-meta">
            Clip: <strong>{formatSeconds(activeStart)} – {formatSeconds(activeEnd)}</strong>
            <em className="hv-timeline-trim-hint">
              {isDraftMode ? ' · new step — drag handles to trim' : ' · drag handles to trim'}
            </em>
          </span>
        )}
      </div>
    </div>
  );
};

const EditableReprocessOutput = ({ result, onChange, onApprove, applying }) => {
  if (!result?.action_step) return null;

  const step = result.action_step;
  const suggestedObjects = result.suggested_objects || step.objects_expected || [];

  const updateStep = (patch) => onChange({
    ...result,
    action_step: { ...step, ...patch },
    suggested_objects: patch.objects_expected || result.suggested_objects
  });

  const updatePrompt = (prompt) => onChange({
    ...result,
    per_step_prompt: { ...result.per_step_prompt, prompt }
  });

  return (
    <section className="hv-card hv-ai-card">
      <div className="hv-card-head hv-ai-card-head">
        <div>
          <span className="hv-step-tag hv-step-tag-ai">Step 4 · Approve</span>
          <p className="hv-card-desc mb-0">Edit the generated output below. Object names are suggested from the clip — adjust before approving.</p>
        </div>
        <button type="button" className="btn btn-primary hv-btn-approve" onClick={onApprove} disabled={applying}>
          <i className="bi bi-check2-circle me-1" />{applying ? 'Saving…' : 'Approve & save'}
        </button>
      </div>
      {suggestedObjects.length > 0 && (
        <div className="hv-suggested-objects">
          <span className="hv-suggested-label">Suggested objects</span>
          <div className="hv-suggested-pills">
            {suggestedObjects.map((name) => (
              <span key={name} className="hv-suggested-pill">{name}</span>
            ))}
          </div>
        </div>
      )}
      <div className="hv-ai-edit-grid">
        <div className="hv-field">
          <label className="form-label">Title</label>
          <input type="text" className="form-control" value={step.title || ''}
            onChange={(e) => updateStep({ title: e.target.value })} />
        </div>
        <div className="hv-field">
          <label className="form-label">Expected objects (comma-separated)</label>
          <input type="text" className="form-control"
            value={(step.objects_expected || []).join(', ')}
            onChange={(e) => updateStep({
              objects_expected: e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
            })} />
        </div>
        <div className="hv-field hv-field-full">
          <label className="form-label">Step context</label>
          <textarea className="form-control" rows="2" value={step.step_context || ''}
            onChange={(e) => updateStep({ step_context: e.target.value })} />
        </div>
        <div className="hv-field hv-field-full">
          <ListEditor label="Success criteria" items={step.success_criteria || []}
            onChange={(items) => updateStep({ success_criteria: items })} addLabel="Add" placeholder="Criterion" />
        </div>
        <div className="hv-field hv-field-full">
          <ListEditor label="Failure modes" items={step.failure_modes || []}
            onChange={(items) => updateStep({ failure_modes: items })} addLabel="Add" placeholder="Failure mode" />
        </div>
        <div className="hv-field hv-field-full">
          <label className="form-label">Reference prompt</label>
          <textarea className="form-control hv-prompt-edit" rows="4"
            value={result.per_step_prompt?.prompt || ''}
            onChange={(e) => updatePrompt(e.target.value)} />
        </div>
      </div>
    </section>
  );
};

const CollapsibleCard = ({ title, subtitle, children, defaultOpen = false, className = '' }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`validation-card hv-collapsible-card ${className}`.trim()}>
      <button
        type="button"
        className="hv-collapsible-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <div>
          <h5 className="mb-1">{title}</h5>
          {subtitle && <p className="mb-0">{subtitle}</p>}
        </div>
        <i className={`bi bi-chevron-${open ? 'up' : 'down'}`}></i>
      </button>
      {open && <div className="hv-collapsible-body">{children}</div>}
    </section>
  );
};

const HITL_WORKFLOW_STEPS = [
  { id: 1, label: 'Watch', icon: 'bi-play-circle' },
  { id: 2, label: 'Correct', icon: 'bi-pencil-square' },
  { id: 3, label: 'Reprocess', icon: 'bi-cpu' },
  { id: 4, label: 'Approve', icon: 'bi-check2-square' }
];

const WorkflowSteps = ({ activePhase, reprocessing, hasAiOutput }) => (
  <ol className="hv-workflow-steps" aria-label="Validation steps">
    {HITL_WORKFLOW_STEPS.map((step) => {
      const isDone = step.id < activePhase;
      const isCurrent = step.id === activePhase;
      const isBusy = step.id === 3 && reprocessing;
      return (
        <li
          key={step.id}
          className={`hv-workflow-step ${isDone ? 'done' : ''} ${isCurrent ? 'current' : ''} ${isBusy ? 'busy' : ''}`}
        >
          <span className="hv-workflow-step-num">
            {isDone ? <i className="bi bi-check-lg" /> : isBusy ? <span className="spinner-border spinner-border-sm" /> : step.id}
          </span>
          <span className="hv-workflow-step-label">{step.label}</span>
        </li>
      );
    })}
    {hasAiOutput && <li className="hv-workflow-ready"><i className="bi bi-stars" /> Ready to approve</li>}
  </ol>
);

const HumanChangesSummary = ({ baseline, edited }) => {
  const changes = summarizeActionStepChanges(baseline, edited);
  if (!changes.length) return null;

  return (
    <div className="hv-changes-summary">
      <span className="hv-changes-label"><i className="bi bi-pencil-square" /> Your changes vs original</span>
      <ul className="hv-changes-list">
        {changes.map((change) => (
          <li key={change.field}>
            <strong>{change.field}</strong>
            {Array.isArray(change.from) ? (
              <span> {JSON.stringify(change.from)} → {JSON.stringify(change.to)}</span>
            ) : (
              <span> &quot;{change.from}&quot; → &quot;{change.to}&quot;</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const GlobalOntologyBar = ({
  renames,
  reprocessing,
  onApply,
  disabled
}) => {
  if (!renames.length) return null;

  return (
    <div className="hv-ontology-bar">
      <div className="hv-ontology-bar-text">
        <span className="hv-step-tag hv-step-tag-muted">Global ontology</span>
        <div>
          <strong>Object rename detected</strong>
          <p>
            {renames.map((r) => (
              <span key={`${r.from}-${r.to}`} className="hv-rename-pill">
                {r.from} → {r.to}
              </span>
            ))}
            {' '}Apply to update all action steps, prompts, and reasoning.
          </p>
        </div>
      </div>
      <button
        type="button"
        className="btn btn-primary hv-btn-ontology"
        onClick={onApply}
        disabled={disabled || reprocessing}
      >
        {reprocessing ? (
          <><span className="spinner-border spinner-border-sm me-2" /> Updating…</>
        ) : (
          <><i className="bi bi-diagram-3 me-2" /> Change global ontology</>
        )}
      </button>
    </div>
  );
};

const ReprocessActionBar = ({
  onReprocess,
  reprocessing,
  disabled,
  segmentStart,
  segmentEnd,
  hasPendingOutput
}) => (
  <div className="hv-reprocess-bar">
    <div className="hv-reprocess-bar-text">
      <span className="hv-step-tag hv-step-tag-ai">Step 3</span>
      <div>
        <strong>Reprocess action step</strong>
        <p>
          Sends your corrections and the selected clip segment for review.
          {segmentStart !== null && segmentEnd !== null && (
            <> Clip: <strong>{formatSeconds(segmentStart)} – {formatSeconds(segmentEnd)}</strong>.</>
          )}
        </p>
      </div>
    </div>
    <button
      type="button"
      className="btn btn-primary hv-btn-reprocess"
      onClick={onReprocess}
      disabled={disabled || reprocessing}
    >
      {reprocessing ? (
        <><span className="spinner-border spinner-border-sm me-2" /> Processing…</>
      ) : (
        <><i className="bi bi-arrow-repeat me-2" /> Reprocess action step</>
      )}
    </button>
    {hasPendingOutput && !reprocessing && (
      <span className="hv-reprocess-hint"><i className="bi bi-arrow-down me-1" /> Review output below</span>
    )}
  </div>
);

const ActionStepSelector = ({
  actionSteps,
  selectedActionId,
  onSelect,
  onPrev,
  onNext,
  canGoPrev,
  canGoNext,
  onAddNew,
  onDelete,
  isAddingNew,
  newStepDraft,
  addDisabled,
  deletingStepId
}) => (
  <section className="hv-action-selector">
    <div className="hv-action-selector-head">
      <span className="hv-step-tag">Pick action</span>
      <div className="hv-action-selector-nav">
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onPrev} disabled={!canGoPrev || isAddingNew} aria-label="Previous action">
          <i className="bi bi-chevron-left" />
        </button>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onNext} disabled={!canGoNext || isAddingNew} aria-label="Next action">
          <i className="bi bi-chevron-right" />
        </button>
      </div>
    </div>
    <div className="hv-action-list">
      {actionSteps.map((step, idx) => {
        const isActive = !isAddingNew && String(step.id) === String(selectedActionId);
        const isDeleting = String(deletingStepId) === String(step.id);
        return (
          <div key={step.id} className={`hv-action-item ${isActive ? 'active' : ''}`}>
            <button
              type="button"
              className="hv-action-item-select"
              onClick={() => onSelect(step.id)}
              disabled={isAddingNew || isDeleting}
            >
              <span className="hv-action-pill-num">{idx + 1}</span>
              <span className="hv-action-item-title">{step.title || 'Untitled'}</span>
            </button>
            {onDelete && (
              <button
                type="button"
                className="hv-action-item-delete"
                onClick={() => onDelete(step)}
                disabled={isAddingNew || isDeleting}
                aria-label={`Delete action step ${idx + 1}`}
                title="Delete action step"
              >
                {isDeleting ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                ) : (
                  <i className="bi bi-trash" />
                )}
              </button>
            )}
          </div>
        );
      })}
      {isAddingNew && newStepDraft && (
        <div className="hv-action-item active hv-action-item-draft">
          <div className="hv-action-item-select">
            <span className="hv-action-pill-num">+</span>
            <span className="hv-action-item-title">{newStepDraft.title?.trim() || 'New step (draft)'}</span>
          </div>
        </div>
      )}
      {onAddNew && (
        <button
          type="button"
          className="hv-action-add-new"
          onClick={onAddNew}
          disabled={addDisabled || isAddingNew}
        >
          <i className="bi bi-plus-circle" aria-hidden="true" />
          <span>Add new action step</span>
        </button>
      )}
    </div>
  </section>
);

const NewActionStepPanel = ({
  draft,
  onTitleChange,
  onProcess,
  onCancel,
  processing,
  segmentStart,
  segmentEnd
}) => (
  <section className="hv-card hv-new-step-card">
    <div className="hv-card-head">
      <span className="hv-step-tag hv-step-tag-human">New action step</span>
      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancel} disabled={processing}>
        Cancel
      </button>
    </div>
    <p className="hv-card-desc">
      Drag the trim handles on the timeline to select the clip segment, optionally set a title, then Process to generate the action step from the clip.
    </p>
    <div className="hv-new-step-grid">
      <div className="hv-field">
        <label className="form-label" htmlFor="hv-new-step-title">Title (optional)</label>
        <input
          id="hv-new-step-title"
          type="text"
          className="form-control"
          value={draft?.title || ''}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="e.g. Pick up the pipette"
          disabled={processing}
        />
      </div>
      <div className="hv-field hv-new-step-meta">
        <span className="hv-new-step-clip-label">Selected clip</span>
        <strong>{formatSeconds(segmentStart)} – {formatSeconds(segmentEnd)}</strong>
      </div>
    </div>
    <div className="hv-new-step-actions">
      <button
        type="button"
        className="btn btn-primary hv-btn-process-new"
        onClick={onProcess}
        disabled={processing}
      >
        {processing ? (
          <><span className="spinner-border spinner-border-sm me-2" /> Processing…</>
        ) : (
          <><i className="bi bi-magic me-2" /> Process new action step</>
        )}
      </button>
    </div>
  </section>
);

const HumanValidation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const protocolId = searchParams.get('editid') || searchParams.get('protocol_id');
  const stepNumber = Number(searchParams.get('step') || 1);

  const [loading, setLoading] = useState(true);
  const [reprocessing, setReprocessing] = useState(false);
  const [error, setError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [protocol, setProtocol] = useState(null);
  const [steps, setSteps] = useState([]);
  const [validationData, setValidationData] = useState(createDefaultValidationData());
  const [selectedActionId, setSelectedActionId] = useState(null);
  const [fullVideoUrl, setFullVideoUrl] = useState('');
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState('');
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [reprocessResult, setReprocessResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(true);
  const [reprocessingOntology, setReprocessingOntology] = useState(false);
  const [pendingGlobalRenames, setPendingGlobalRenames] = useState([]);
  const [newStepDraft, setNewStepDraft] = useState(null);
  const [processingNewStep, setProcessingNewStep] = useState(false);
  const [deletingStepId, setDeletingStepId] = useState(null);
  const [validationSummary, setValidationSummary] = useState(null);
  const [markingValidation, setMarkingValidation] = useState(false);

  const videoRef = useRef(null);
  const baselineObjectsRef = useRef([]);
  const baselineActionStepsRef = useRef({});
  const originalBaselineObjectsRef = useRef([]);
  const originalBaselineActionStepsRef = useRef({});
  const objectNameAtFocusRef = useRef({});
  const actionStepObjectAtFocusRef = useRef({});
  const titleAtFocusRef = useRef('');
  const pendingRenamesRef = useRef([]);

  const fetchPageData = useCallback(async () => {
    if (!protocolId || !stepNumber) {
      setError('Protocol id or step number is missing.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const [protocolResponse, validationResponse, statusResponse] = await Promise.all([
        API.get(`/qurator/protocols/${protocolId}`),
        API.get(`/qurator/protocols/${protocolId}/human-validation`, { params: { step_number: stepNumber } }),
        API.get(`/qurator/protocols/${protocolId}/human-validation/status`).catch(() => ({ data: { success: false } }))
      ]);
      setProtocol(protocolResponse.data?.protocol || null);
      setSteps(getOrderedSteps(protocolResponse.data?.protocol));
      if (statusResponse.data?.success) {
        setValidationSummary(statusResponse.data);
      } else {
        setValidationSummary(null);
      }
      const normalized = normalizeValidationData(validationResponse.data?.schema);
      normalized.objects = sanitizeInventoryObjects(normalized.objects);
      normalized.action_steps = normalized.action_steps.map((step) => ({
        ...step,
        objects_expected: normalizeStringArray(step.objects_expected).filter(isValidOntologyObjectName)
      }));
      baselineObjectsRef.current = JSON.parse(JSON.stringify(normalized.objects));
      baselineActionStepsRef.current = Object.fromEntries(
        normalized.action_steps.map((step) => [String(step.id), JSON.parse(JSON.stringify(step))])
      );
      originalBaselineObjectsRef.current = JSON.parse(JSON.stringify(normalized.objects));
      originalBaselineActionStepsRef.current = Object.fromEntries(
        normalized.action_steps.map((step) => [String(step.id), JSON.parse(JSON.stringify(step))])
      );
      setValidationData(normalized);
      setSelectedActionId(normalized.action_steps[0]?.id ?? null);
      setReprocessResult(null);
    } catch (fetchError) {
      console.error('Failed to load human validation data:', fetchError);
      setError(fetchError.response?.data?.message || 'Failed to load human validation data.');
    } finally {
      setLoading(false);
    }
  }, [protocolId, stepNumber]);

  const refreshValidationSummary = useCallback(async () => {
    if (!protocolId) return null;
    try {
      const response = await API.get(`/qurator/protocols/${protocolId}/human-validation/status`);
      if (response.data?.success) {
        setValidationSummary(response.data);
        return response.data;
      }
    } catch (summaryError) {
      console.error('Failed to refresh validation summary:', summaryError);
    }
    return null;
  }, [protocolId]);

  const isCurrentStepValidated = useMemo(() => {
    const match = validationSummary?.steps?.find((entry) => Number(entry.step_number) === Number(stepNumber));
    return Number(match?.human_validation) === 1;
  }, [validationSummary, stepNumber]);

  const loadFullVideo = useCallback(async () => {
    if (!protocolId || !stepNumber) return;
    setVideoLoading(true);
    setVideoError('');
    setFullVideoUrl('');

    try {
      const defaultKey = `uploads/${protocolId}/${protocolId}_${stepNumber}.mp4`;
      const response = await API.get(`/qurator/protocols/${protocolId}/files/presigned-url`, {
        params: { file_key: defaultKey }
      });
      if (!response.data?.success || !response.data?.url) {
        throw new Error('Video URL could not be generated.');
      }
      setFullVideoUrl(response.data.url);
    } catch (primaryError) {
      try {
        const materialsResponse = await API.get(`/qurator/protocols/${protocolId}/reference-materials`, {
          params: { step_number: stepNumber }
        });
        const fallbackKey = materialsResponse.data?.materials?.videos?.[0]?.key;
        if (!fallbackKey) throw primaryError;
        const response = await API.get(`/qurator/protocols/${protocolId}/files/presigned-url`, {
          params: { file_key: fallbackKey }
        });
        setFullVideoUrl(response.data.url);
      } catch (loadError) {
        setVideoError(loadError.response?.data?.message || loadError.message || 'No step video found.');
      }
    } finally {
      setVideoLoading(false);
    }
  }, [protocolId, stepNumber]);

  useEffect(() => { fetchPageData(); }, [fetchPageData]);
  useEffect(() => { loadFullVideo(); }, [loadFullVideo]);

  useEffect(() => {
    if (!protocolId || !stepNumber) return;
    const key = DISCLAIMER_STORAGE_KEY(protocolId, stepNumber);
    setDisclaimerAccepted(sessionStorage.getItem(key) === '1');
  }, [protocolId, stepNumber]);

  const acceptDisclaimer = () => {
    if (protocolId && stepNumber) {
      sessionStorage.setItem(DISCLAIMER_STORAGE_KEY(protocolId, stepNumber), '1');
    }
    setDisclaimerAccepted(true);
  };

  useEffect(() => {
    if (validationData.action_steps.length === 0) {
      if (selectedActionId !== null) setSelectedActionId(null);
      return;
    }
    const exists = validationData.action_steps.some((s) => String(s.id) === String(selectedActionId));
    if (!exists) setSelectedActionId(validationData.action_steps[0].id);
  }, [validationData.action_steps, selectedActionId]);

  const selectedActionStep = useMemo(
    () => validationData.action_steps.find((s) => String(s.id) === String(selectedActionId)) || null,
    [selectedActionId, validationData.action_steps]
  );

  const selectedTimeline = useMemo(
    () => validationData.timeline.find((e) => String(e.step_id) === String(selectedActionId)) || null,
    [selectedActionId, validationData.timeline]
  );

  const selectedPrompt = useMemo(
    () => validationData.reference_prompts.per_step.find((e) => String(e.step_id) === String(selectedActionId)) || null,
    [selectedActionId, validationData.reference_prompts.per_step]
  );

  const activeStepObjectNames = useMemo(() => {
    const names = normalizeStringArray(selectedActionStep?.objects_expected).filter(isValidOntologyObjectName);
    return new Set(names.map((name) => name.toLowerCase()));
  }, [selectedActionStep?.objects_expected]);

  const workflowPhase = useMemo(() => {
    if (applying) return 4;
    if (reprocessResult) return 4;
    if (reprocessing || processingNewStep) return 3;
    if (newStepDraft) return 2;
    return 2;
  }, [applying, reprocessResult, reprocessing, processingNewStep, newStepDraft]);

  const isAddingNewStep = Boolean(newStepDraft);
  const segmentStart = isAddingNewStep
    ? Number(newStepDraft?.approx_start_s) || 0
    : Number(selectedTimeline?.approx_start_s) || 0;
  const segmentEnd = isAddingNewStep
    ? Number(newStepDraft?.approx_end_s) || videoDuration
    : Number(selectedTimeline?.approx_end_s) || videoDuration;
  const hasValidSegment = Number.isFinite(segmentEnd) && segmentEnd > segmentStart;

  const startSegmentPlayback = useCallback((shouldPlay = true) => {
    const video = videoRef.current;
    if (!video || !fullVideoUrl || !Number.isFinite(segmentStart)) {
      return;
    }

    video.currentTime = segmentStart;
    setCurrentTime(segmentStart);

    if (shouldPlay && hasValidSegment) {
      video.play().catch(() => {});
    }
  }, [fullVideoUrl, hasValidSegment, segmentStart]);

  useEffect(() => {
    startSegmentPlayback(true);
  }, [selectedActionId, fullVideoUrl, segmentStart, segmentEnd, startSegmentPlayback, newStepDraft]);

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    const time = video.currentTime;
    setCurrentTime(time);

    if (hasValidSegment && time >= segmentEnd - 0.05) {
      video.currentTime = segmentStart;
      setCurrentTime(segmentStart);
      if (video.paused) {
        video.play().catch(() => {});
      }
    }
  };

  const handleVideoLoadedMetadata = (event) => {
    const video = event.currentTarget;
    const duration = video.duration || 0;
    setVideoDuration(duration);

    const start = Number(selectedTimeline?.approx_start_s) || 0;
    const end = Number(selectedTimeline?.approx_end_s) || duration;
    video.currentTime = start;
    setCurrentTime(start);

    if (end > start) {
      video.play().catch(() => {});
      setVideoPlaying(true);
    }
  };

  const handleSeek = (time) => {
    const video = videoRef.current;
    if (!video) return;
    const clamped = Math.min(segmentEnd, Math.max(segmentStart, time));
    video.currentTime = clamped;
    setCurrentTime(clamped);
  };

  const clampPlaybackToSegment = () => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(segmentStart) || !Number.isFinite(segmentEnd)) return;
    if (video.currentTime < segmentStart || video.currentTime > segmentEnd) {
      const clamped = Math.min(segmentEnd, Math.max(segmentStart, video.currentTime));
      video.currentTime = clamped;
      setCurrentTime(clamped);
    }
  };

  const currentStepText = steps[stepNumber - 1] || '';
  const totalSteps = steps.length;
  const actionIndex = validationData.action_steps.findIndex((s) => String(s.id) === String(selectedActionId));
  const canGoPrevAction = actionIndex > 0;
  const canGoNextAction = actionIndex >= 0 && actionIndex < validationData.action_steps.length - 1;

  const updateValidationData = (updater, { sanitize = true } = {}) => {
    setValidationData((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return sanitize ? normalizeValidationData(next) : next;
    });
    if (sanitize) setReprocessResult(null);
  };

  const updateSelectedActionStep = useCallback((updater, options = {}) => {
    if (selectedActionId === null) return;
    updateValidationData((prev) => ({
      ...prev,
      action_steps: prev.action_steps.map((s) => (
        String(s.id) === String(selectedActionId) ? updater(s) : s
      ))
    }), options);
  }, [selectedActionId]);

  const baselineSelectedActionStep = originalBaselineActionStepsRef.current[String(selectedActionId)] || null;

  const detectedGlobalRenames = useMemo(
    () => buildGlobalObjectRenames(originalBaselineObjectsRef.current, validationData.objects),
    [validationData.objects]
  );

  const allGlobalRenames = useMemo(() => {
    const merged = [...pendingGlobalRenames];
    detectedGlobalRenames.forEach((r) => {
      if (!merged.some((m) => namesMatch(m.from, r.from))) merged.push(r);
    });
    return merged;
  }, [pendingGlobalRenames, detectedGlobalRenames]);

  const handleActionStepTitleFocus = () => {
    titleAtFocusRef.current = selectedActionStep?.title ?? '';
  };

  const handleActionStepTitleBlur = () => {
    titleAtFocusRef.current = '';
  };

  const persistValidationSchema = useCallback(async (schema) => {
    const payload = stripDraftRowsForSave(normalizeValidationData(schema));
    await API.put(`/qurator/protocols/${protocolId}/human-validation`, {
      step_number: stepNumber,
      schema: payload
    });
    const cleanObjects = sanitizeInventoryObjects(
      (schema.objects || []).map(({ _key, ...item }) => item)
    );
    baselineObjectsRef.current = JSON.parse(JSON.stringify(cleanObjects));
    baselineActionStepsRef.current = Object.fromEntries(
      schema.action_steps.map((step) => [String(step.id), JSON.parse(JSON.stringify(step))])
    );
    originalBaselineObjectsRef.current = JSON.parse(JSON.stringify(cleanObjects));
    originalBaselineActionStepsRef.current = Object.fromEntries(
      schema.action_steps.map((step) => [String(step.id), JSON.parse(JSON.stringify(step))])
    );
  }, [protocolId, stepNumber]);

  const updateSelectedTimelineTrim = useCallback((stepId, patch) => {
    updateValidationData((prev) => {
      const idx = prev.timeline.findIndex((e) => String(e.step_id) === String(stepId));
      const nextTimeline = [...prev.timeline];
      const entry = idx >= 0
        ? { ...nextTimeline[idx] }
        : { step_id: stepId, approx_start_s: '', approx_end_s: '' };
      if (patch.approx_start_s !== undefined) {
        entry.approx_start_s = typeof patch.approx_start_s === 'number'
          ? patch.approx_start_s
          : toNumberOrEmpty(patch.approx_start_s);
      }
      if (patch.approx_end_s !== undefined) {
        entry.approx_end_s = typeof patch.approx_end_s === 'number'
          ? patch.approx_end_s
          : toNumberOrEmpty(patch.approx_end_s);
      }
      if (idx >= 0) nextTimeline[idx] = entry;
      else nextTimeline.push(entry);
      return { ...prev, timeline: nextTimeline };
    });
  }, []);

  const updateNewStepDraftTrim = useCallback((patch) => {
    setNewStepDraft((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      if (patch.approx_start_s !== undefined) next.approx_start_s = patch.approx_start_s;
      if (patch.approx_end_s !== undefined) next.approx_end_s = patch.approx_end_s;
      return next;
    });
  }, []);

  const handleStartNewActionStep = () => {
    if (!fullVideoUrl) {
      setError('Load the video before adding a new action step.');
      return;
    }
    const dur = videoDuration || Number(validationData.video_meta.duration_hint_s) || 30;
    const nextId = getNextActionStepId(validationData.action_steps);
    const trim = suggestNewStepTrim(validationData.timeline, dur);
    setNewStepDraft({
      stepId: nextId,
      approx_start_s: trim.start,
      approx_end_s: trim.end,
      title: ''
    });
    setReprocessResult(null);
    setError('');
    setSaveMessage('');
  };

  const cancelNewActionStep = () => {
    setNewStepDraft(null);
    setReprocessResult(null);
  };

  const handleDeleteActionStep = async (step) => {
    if (!step || !protocolId) return;

    const stepTitle = (step.title || '').trim() || `Action ${step.id}`;
    const confirm = await Swal.fire({
      title: 'Delete action step?',
      text: `Remove "${stepTitle}" from this protocol step? This also removes its timeline segment and reference prompt.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel'
    });

    if (!confirm.isConfirmed) return;

    const stepIdStr = String(step.id);

    try {
      setDeletingStepId(step.id);
      setError('');
      setSaveMessage('');

      const nextData = normalizeValidationData(validationData);
      const remainingSteps = nextData.action_steps.filter((s) => String(s.id) !== stepIdStr);

      const updatedData = {
        ...nextData,
        action_steps: remainingSteps,
        timeline: nextData.timeline.filter((entry) => String(entry.step_id) !== stepIdStr),
        reference_prompts: {
          ...nextData.reference_prompts,
          per_step: nextData.reference_prompts.per_step.filter((entry) => String(entry.step_id) !== stepIdStr)
        }
      };

      await persistValidationSchema(updatedData);
      setValidationData(updatedData);

      if (String(selectedActionId) === stepIdStr) {
        const deletedIndex = validationData.action_steps.findIndex((s) => String(s.id) === stepIdStr);
        const nextSelected = remainingSteps[Math.min(deletedIndex, remainingSteps.length - 1)]
          || remainingSteps[0]
          || null;
        setSelectedActionId(nextSelected?.id ?? null);
      }

      if (reprocessResult?.action_step && String(reprocessResult.action_step.id) === stepIdStr) {
        setReprocessResult(null);
      }

      delete baselineActionStepsRef.current[stepIdStr];
      delete originalBaselineActionStepsRef.current[stepIdStr];
      baselineActionStepsRef.current = Object.fromEntries(
        updatedData.action_steps.map((s) => [String(s.id), JSON.parse(JSON.stringify(s))])
      );
      originalBaselineActionStepsRef.current = { ...baselineActionStepsRef.current };

      setSaveMessage('Action step removed.');
      await refreshValidationSummary();
    } catch (deleteError) {
      console.error('Delete action step failed:', deleteError);
      setError(deleteError.response?.data?.message || deleteError.message || 'Failed to delete action step.');
    } finally {
      setDeletingStepId(null);
    }
  };

  const handleProcessNewActionStep = async () => {
    if (!newStepDraft || !protocolId) return;

    const start = Number(newStepDraft.approx_start_s);
    const end = Number(newStepDraft.approx_end_s);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
      setError('Set valid clip start/end times for the new action step.');
      return;
    }

    try {
      setProcessingNewStep(true);
      setError('');
      setSaveMessage('');

      const draftSchema = normalizeValidationData(validationData);
      const response = await API.post(`/qurator/protocols/${protocolId}/human-validation/process-new-action-step`, {
        step_number: stepNumber,
        action_step_id: newStepDraft.stepId,
        schema: stripDraftRowsForSave(draftSchema),
        global_objects: draftSchema.objects.map(({ _key, ...item }) => item),
        global_context: draftSchema.reference_prompts?.global_context || '',
        timeline_entry: {
          step_id: newStepDraft.stepId,
          approx_start_s: start,
          approx_end_s: end
        },
        optional_title: newStepDraft.title?.trim() || ''
      });

      if (!response.data?.success || !response.data?.result) {
        throw new Error(response.data?.message || 'Failed to process new action step');
      }

      setReprocessResult({
        ...response.data.result,
        isNewStep: true,
        newStepTimeline: {
          step_id: newStepDraft.stepId,
          approx_start_s: start,
          approx_end_s: end
        },
        suggested_objects: response.data.suggested_objects
          || response.data.result?.suggested_objects
          || response.data.result?.action_step?.objects_expected
          || []
      });
      setSaveMessage('New action step generated. Review the output and press Approve & save.');
    } catch (newStepError) {
      console.error('Process new action step failed:', newStepError);
      setError(newStepError.response?.data?.message || newStepError.message || 'Failed to process new action step.');
    } finally {
      setProcessingNewStep(false);
    }
  };

  const handleDoneValidation = async () => {
    if (!protocolId || !stepNumber) return;

    const confirm = await Swal.fire({
      title: 'Mark step as validated?',
      text: 'This step will be marked complete. Save any current review work and continue when ready.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, mark validated',
      cancelButtonText: 'Cancel'
    });

    if (!confirm.isConfirmed) return;

    try {
      setMarkingValidation(true);
      setError('');
      setSaveMessage('');

      await persistValidationSchema(validationData);
      const response = await API.put(`/qurator/protocols/${protocolId}/human-validation/mark-step-complete`, {
        step_number: stepNumber
      });

      if (response.data?.success) {
        setValidationSummary(response.data);
        setSaveMessage(response.data.is_fully_validated
          ? 'All steps validated for this experiment.'
          : `Step ${stepNumber} marked as validated.`);
      }
    } catch (doneError) {
      console.error('Done validation failed:', doneError);
      setError(doneError.response?.data?.message || doneError.message || 'Failed to mark step as validated.');
    } finally {
      setMarkingValidation(false);
    }
  };

  const toggleVideoPlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      if (video.currentTime < segmentStart || video.currentTime >= segmentEnd) {
        video.currentTime = segmentStart;
        setCurrentTime(segmentStart);
      }
      video.play().catch(() => {});
      setVideoPlaying(true);
    } else {
      video.pause();
      setVideoPlaying(false);
    }
  };

  const updateSelectedTimeline = useCallback((field, value) => {
    if (selectedActionId === null) return;
    const nextValue = value === '' ? '' : toNumberOrEmpty(value);
    updateValidationData((prev) => {
      const idx = prev.timeline.findIndex((e) => String(e.step_id) === String(selectedActionId));
      const nextTimeline = [...prev.timeline];
      const entry = idx >= 0
        ? { ...nextTimeline[idx] }
        : { step_id: selectedActionId, approx_start_s: '', approx_end_s: '' };
      entry[field] = nextValue;
      if (idx >= 0) nextTimeline[idx] = entry;
      else nextTimeline.push(entry);
      return { ...prev, timeline: nextTimeline };
    });
  }, [selectedActionId]);

  const updateSelectedPrompt = useCallback((value) => {
    if (selectedActionId === null) return;
    updateValidationData((prev) => {
      const idx = prev.reference_prompts.per_step.findIndex((e) => String(e.step_id) === String(selectedActionId));
      const nextPrompts = [...prev.reference_prompts.per_step];
      const entry = idx >= 0 ? { ...nextPrompts[idx] } : { step_id: selectedActionId, prompt: '' };
      entry.prompt = value;
      if (idx >= 0) nextPrompts[idx] = entry;
      else nextPrompts.push(entry);
      return {
        ...prev,
        reference_prompts: { ...prev.reference_prompts, per_step: nextPrompts }
      };
    });
  }, [selectedActionId]);

  const handleObjectNameInput = (index, newName) => {
    updateValidationData((prev) => ({
      ...prev,
      objects: prev.objects.map((item, i) => (i === index ? { ...item, name: newName } : item))
    }), { sanitize: false });
  };

  const handleObjectNameFocus = (index) => {
    objectNameAtFocusRef.current[index] = validationData.objects[index]?.name ?? '';
  };

  const handleObjectNameBlur = (index) => {
    const fromName = (objectNameAtFocusRef.current[index] ?? '').trim();
    delete objectNameAtFocusRef.current[index];

    updateValidationData((prev) => {
      const toName = (prev.objects[index]?.name ?? '').trim();

      if (!toName) {
        if (isValidObjectName(fromName)) {
          return removeObjectFromGlobalOntology(prev, fromName);
        }
        return prev;
      }

      if (fromName && toName && !namesMatch(fromName, toName) && isValidObjectName(toName)) {
        setPendingGlobalRenames((current) => {
          if (current.some((r) => namesMatch(r.from, fromName))) return current;
          return [...current, { from: fromName, to: toName }];
        });
        return {
          ...prev,
          objects: prev.objects.map((item, i) => (i === index ? { ...item, name: toName } : item))
        };
      }

      return prev;
    }, { sanitize: false });
  };

  const handleRemoveOntologyObject = (index) => {
    const objectName = validationData.objects[index]?.name?.trim() || '';

    updateValidationData((prev) => {
      if (!isValidObjectName(objectName)) {
        return { ...prev, objects: prev.objects.filter((_, i) => i !== index) };
      }
      return removeObjectFromGlobalOntology(prev, objectName);
    });
  };

  const handleActionStepObjectFocus = (index) => {
    const current = validationData.action_steps.find(
      (step) => String(step.id) === String(selectedActionId)
    );
    actionStepObjectAtFocusRef.current[index] = current?.objects_expected?.[index] ?? '';
  };

  const handleActionStepObjectBlur = (index) => {
    delete actionStepObjectAtFocusRef.current[index];
  };

  const handleRemoveExpectedObject = (index) => {
    if (selectedActionId === null) return;

    updateValidationData((prev) => {
      const step = prev.action_steps.find((s) => String(s.id) === String(selectedActionId));
      const currentExpected = normalizeStringArray(step?.objects_expected);
      const nextExpected = currentExpected.filter((_, i) => i !== index);

      return {
        ...prev,
        action_steps: prev.action_steps.map((s) => (
          String(s.id) === String(selectedActionId)
            ? { ...s, objects_expected: nextExpected.length ? nextExpected : [''] }
            : s
        ))
      };
    });
  };

  const handleReprocessGlobalOntology = async () => {
    if (!protocolId || !allGlobalRenames.length) return;

    try {
      setReprocessingOntology(true);
      setError('');
      setSaveMessage('');

      const schemaPayload = stripDraftRowsForSave(normalizeValidationData(validationData));
      const response = await API.post(`/qurator/protocols/${protocolId}/human-validation/reprocess-ontology`, {
        step_number: stepNumber,
        schema: schemaPayload,
        baseline_objects: originalBaselineObjectsRef.current,
        object_renames: allGlobalRenames
      });

      if (!response.data?.success || !response.data?.schema) {
        throw new Error(response.data?.message || 'Global ontology reprocess failed');
      }

      const updated = normalizeValidationData(response.data.schema);
      updated.objects = normalizeInventoryForEdit(updated.objects);
      await persistValidationSchema(updated);
      setValidationData(updated);
      baselineObjectsRef.current = JSON.parse(JSON.stringify(updated.objects));
      originalBaselineObjectsRef.current = JSON.parse(JSON.stringify(
        sanitizeInventoryObjects(updated.objects.map(({ _key, ...item }) => item))
      ));
      baselineActionStepsRef.current = Object.fromEntries(
        updated.action_steps.map((step) => [String(step.id), JSON.parse(JSON.stringify(step))])
      );
      originalBaselineActionStepsRef.current = { ...baselineActionStepsRef.current };
      setPendingGlobalRenames([]);
      setReprocessResult(null);
      setSaveMessage('Global ontology updated across all action steps.');
      await refreshValidationSummary();
    } catch (ontologyError) {
      console.error('Global ontology reprocess failed:', ontologyError);
      setError(ontologyError.response?.data?.message || ontologyError.message || 'Global ontology reprocess failed.');
    } finally {
      setReprocessingOntology(false);
    }
  };

  const handleReprocess = async () => {
    if (!selectedActionStep || !protocolId) return;

    const start = Number(selectedTimeline?.approx_start_s);
    const end = Number(selectedTimeline?.approx_end_s);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
      setError('Set valid clip start/end times for this action step before reprocessing.');
      return;
    }

    try {
      setReprocessing(true);
      setError('');
      setSaveMessage('');

      const baselineStep = originalBaselineActionStepsRef.current[String(selectedActionStep.id)] || null;
      const draftSchema = normalizeValidationData(validationData);
      const draftStepFinal = draftSchema.action_steps.find(
        (step) => String(step.id) === String(selectedActionStep.id)
      ) || selectedActionStep;
      const stepForReprocess = {
        ...draftStepFinal,
        objects_expected: normalizeStringArray(draftStepFinal.objects_expected).filter(Boolean)
      };

      const humanChanges = summarizeActionStepChanges(baselineStep, stepForReprocess);

      const response = await API.post(`/qurator/protocols/${protocolId}/human-validation/reprocess`, {
        step_number: stepNumber,
        action_step_id: selectedActionStep.id,
        schema: stripDraftRowsForSave(draftSchema),
        global_objects: draftSchema.objects.map(({ _key, ...item }) => item),
        global_context: draftSchema.reference_prompts?.global_context || '',
        video_meta: draftSchema.video_meta,
        timeline_entry: draftSchema.timeline.find(
          (entry) => String(entry.step_id) === String(selectedActionStep.id)
        ) || null,
        action_step: stepForReprocess,
        baseline_action_step: baselineStep,
        human_changes: humanChanges,
        baseline_objects: originalBaselineObjectsRef.current,
        object_renames: []
      });

      if (!response.data?.success || !response.data?.result) {
        throw new Error(response.data?.message || 'Reprocess failed');
      }

      setReprocessResult({
        ...response.data.result,
        suggested_objects: response.data.suggested_objects
          || response.data.result?.suggested_objects
          || response.data.result?.action_step?.objects_expected
          || []
      });
      pendingRenamesRef.current = response.data.object_renames_applied || [];
      setSaveMessage('Reprocess complete. Review the output and press Approve & save.');
    } catch (reprocessError) {
      console.error('Reprocess failed:', reprocessError);
      setError(reprocessError.response?.data?.message || reprocessError.message || 'Reprocess failed.');
    } finally {
      setReprocessing(false);
    }
  };

  const applyReprocessResult = async () => {
    if (!reprocessResult?.action_step || !protocolId) return;

    const confirm = await Swal.fire({
      title: 'Save changes?',
      text: 'Are you sure? These settings cannot be reverted once saved.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, save',
      cancelButtonText: 'No'
    });

    if (!confirm.isConfirmed) return;

    const isNewStep = Boolean(reprocessResult.isNewStep);
    const targetStepId = isNewStep
      ? reprocessResult.action_step.id
      : selectedActionId;
    if (!isNewStep && targetStepId === null) return;

    const { action_step: updatedStep, per_step_prompt: updatedPrompt, objects: updatedObjects } = reprocessResult;

    try {
      setApplying(true);
      setError('');
      setSaveMessage('');

      let nextData = normalizeValidationData(validationData);
      const mergedStep = { ...updatedStep, id: targetStepId };

      const promptEntry = {
        step_id: targetStepId,
        prompt: updatedPrompt?.prompt || ''
      };

      const mergedObjects = Array.isArray(updatedObjects) && updatedObjects.length
        ? updatedObjects
        : mergeObjectsInventory(
          originalBaselineObjectsRef.current,
          nextData.objects,
          mergedStep.objects_expected
        );

      if (isNewStep) {
        const timelineEntry = reprocessResult.newStepTimeline || {
          step_id: targetStepId,
          approx_start_s: newStepDraft?.approx_start_s ?? '',
          approx_end_s: newStepDraft?.approx_end_s ?? ''
        };
        nextData = {
          ...nextData,
          objects: mergedObjects,
          action_steps: [...nextData.action_steps, mergedStep],
          timeline: [...nextData.timeline, timelineEntry],
          reference_prompts: {
            ...nextData.reference_prompts,
            per_step: [...nextData.reference_prompts.per_step, promptEntry]
          }
        };
      } else {
        const nextActionSteps = nextData.action_steps.map((s) => (
          String(s.id) === String(targetStepId) ? mergedStep : s
        ));

        const promptIdx = nextData.reference_prompts.per_step.findIndex(
          (e) => String(e.step_id) === String(targetStepId)
        );
        const nextPerStep = [...nextData.reference_prompts.per_step];
        const existingPromptEntry = promptIdx >= 0
          ? { ...nextPerStep[promptIdx] }
          : { step_id: targetStepId, prompt: '' };
        existingPromptEntry.prompt = updatedPrompt?.prompt || existingPromptEntry.prompt;
        if (promptIdx >= 0) nextPerStep[promptIdx] = existingPromptEntry;
        else nextPerStep.push(existingPromptEntry);

        nextData = {
          ...nextData,
          objects: mergedObjects,
          action_steps: nextActionSteps,
          reference_prompts: { ...nextData.reference_prompts, per_step: nextPerStep }
        };
      }

      await persistValidationSchema(nextData);
      setValidationData(nextData);
      baselineActionStepsRef.current[String(targetStepId)] = JSON.parse(JSON.stringify(
        nextData.action_steps.find((s) => String(s.id) === String(targetStepId))
      ));
      baselineObjectsRef.current = JSON.parse(JSON.stringify(nextData.objects));
      if (isNewStep) {
        setNewStepDraft(null);
        setSelectedActionId(targetStepId);
      }
      pendingRenamesRef.current = [];
      setReprocessResult(null);
      await refreshValidationSummary();
      setSaveMessage(isNewStep
        ? 'New action step saved.'
        : 'Changes saved successfully.');
    } catch (applyError) {
      console.error('Apply reprocess failed:', applyError);
      setError(applyError.response?.data?.message || applyError.message || 'Failed to save changes.');
    } finally {
      setApplying(false);
    }
  };

  const moveToStep = (nextStep) => {
    navigate(`/human-validation?editid=${protocolId}&step=${nextStep}`);
  };

  const selectActionByOffset = (offset) => {
    const idx = validationData.action_steps.findIndex((s) => String(s.id) === String(selectedActionId));
    const next = validationData.action_steps[idx + offset];
    if (next) setSelectedActionId(next.id);
  };

  if (loading) {
    return (
      <QuratorLayout>
        <div className="human-validation-page hv-page">
          <div className="hv-loading">
            <div className="hv-loading-ring">
              <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
            </div>
            <h3>Preparing validation workspace</h3>
            <p className="mb-0">Loading protocol data, video, and action steps…</p>
          </div>
        </div>
      </QuratorLayout>
    );
  }

  return (
    <QuratorLayout>
      <div className="human-validation-page hv-page">
        {!disclaimerAccepted && <ValidationDisclaimerModal onAccept={acceptDisclaimer} />}

        <div className={!disclaimerAccepted ? 'hv-content-blurred' : ''}>
          {/* Top bar */}
          <header className="hv-topbar">
            <div className="hv-topbar-left">
              <button type="button" className="btn btn-link hv-back-link" onClick={() => navigate(`/draft-edit?editid=${protocolId}`)}>
                <i className="bi bi-arrow-left" /> Back to draft
              </button>
              <div className="hv-topbar-title">
                <h1>{protocol?.draft_title || 'Human Validation'}</h1>
                <span className="hv-topbar-meta">
                  Step {stepNumber} of {totalSteps || '—'} · Human-in-the-loop review
                  {validationSummary && (
                    <> · {validationSummary.validated_steps}/{validationSummary.total_steps} validated</>
                  )}
                </span>
              </div>
            </div>
            <div className="hv-topbar-actions">
              {isCurrentStepValidated ? (
                <span className="hv-validated-badge">
                  <i className="bi bi-check-circle-fill" /> Step validated
                </span>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary hv-btn-done-validation"
                  onClick={handleDoneValidation}
                  disabled={markingValidation || applying}
                >
                  {markingValidation ? (
                    <><span className="spinner-border spinner-border-sm me-2" /> Saving…</>
                  ) : (
                    <><i className="bi bi-shield-check me-2" /> Done validation</>
                  )}
                </button>
              )}
            </div>
          </header>

          <WorkflowSteps activePhase={workflowPhase} reprocessing={reprocessing || processingNewStep} hasAiOutput={Boolean(reprocessResult)} />

          {error && <div className="hv-alert hv-alert-error" role="alert"><i className="bi bi-exclamation-triangle-fill" /><span>{error}</span></div>}
          {saveMessage && <div className="hv-alert hv-alert-success" role="status"><i className="bi bi-check-circle-fill" /><span>{saveMessage}</span></div>}

          {/* Protocol context */}
          <section className="hv-protocol-bar">
            <i className="bi bi-journal-text" />
            <p>{currentStepText || 'No protocol step text.'}</p>
            {validationSummary?.steps?.length > 0 && (
              <div className="hv-step-validation-pills" aria-label="Step validation progress">
                {validationSummary.steps.map((entry) => {
                  const isActive = Number(entry.step_number) === Number(stepNumber);
                  const isDone = Number(entry.human_validation) === 1;
                  return (
                    <button
                      key={entry.step_number}
                      type="button"
                      className={`hv-step-validation-pill ${isActive ? 'active' : ''} ${isDone ? 'done' : ''}`}
                      onClick={() => moveToStep(entry.step_number)}
                      title={isDone ? `Step ${entry.step_number} validated` : `Step ${entry.step_number} pending`}
                    >
                      {isDone ? <i className="bi bi-check-lg" /> : entry.step_number}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="hv-protocol-nav">
              <button type="button" className="btn btn-sm btn-light" onClick={() => moveToStep(stepNumber - 1)} disabled={stepNumber <= 1}>
                <i className="bi bi-chevron-left" />
              </button>
              <button type="button" className="btn btn-sm btn-light" onClick={() => moveToStep(stepNumber + 1)} disabled={!totalSteps || stepNumber >= totalSteps}>
                <i className="bi bi-chevron-right" />
              </button>
            </div>
          </section>

          {(validationData.action_steps.length > 0 || fullVideoUrl) && (
            <ActionStepSelector
              actionSteps={validationData.action_steps}
              selectedActionId={selectedActionId}
              onSelect={setSelectedActionId}
              onPrev={() => selectActionByOffset(-1)}
              onNext={() => selectActionByOffset(1)}
              canGoPrev={canGoPrevAction}
              canGoNext={canGoNextAction}
              onAddNew={handleStartNewActionStep}
              onDelete={handleDeleteActionStep}
              isAddingNew={isAddingNewStep}
              newStepDraft={newStepDraft}
              addDisabled={processingNewStep || reprocessing || !fullVideoUrl}
              deletingStepId={deletingStepId}
            />
          )}

          {!selectedActionStep && !isAddingNewStep ? (
            <section className="hv-card hv-empty-card">
              <div className="validation-empty-state">
                <i className="bi bi-list-task" />
                <h4>No action steps</h4>
                <p className="mb-3">Process the step video in Edit Draft first, or add a new action step manually.</p>
                {fullVideoUrl && (
                  <button type="button" className="btn btn-primary" onClick={handleStartNewActionStep}>
                    <i className="bi bi-plus-lg me-1" /> Add new action step
                  </button>
                )}
              </div>
            </section>
          ) : (
            <>
              {/* Step 1: Video + ontology side by side */}
              <div className="hv-stage-row">
                <section className="hv-card hv-video-card">
                  <div className="hv-card-head">
                    <span className="hv-step-tag">Step 1 · Watch</span>
                    <div className="hv-video-controls">
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={toggleVideoPlayback}>
                        <i className={`bi bi-${videoPlaying ? 'pause' : 'play'}-fill`} />
                        {videoPlaying ? 'Pause' : 'Play'}
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => startSegmentPlayback(true)}>
                        <i className="bi bi-arrow-repeat" /> Replay clip
                      </button>
                    </div>
                  </div>
                  <div className="validation-video-shell hv-video-frame">
                    {fullVideoUrl ? (
                      <video
                        ref={videoRef}
                        className="validation-video-player"
                        autoPlay
                        muted
                        playsInline
                        src={fullVideoUrl}
                        onLoadedMetadata={handleVideoLoadedMetadata}
                        onTimeUpdate={handleTimeUpdate}
                        onPlay={() => setVideoPlaying(true)}
                        onPause={() => setVideoPlaying(false)}
                        onSeeked={clampPlaybackToSegment}
                      />
                    ) : (
                      <div className="validation-video-empty">
                        {videoLoading ? (
                          <><div className="spinner-border text-light" role="status" /><p className="mb-0 mt-3">Loading video…</p></>
                        ) : (
                          <><i className="bi bi-camera-video-off" /><p className="mb-0">{videoError || 'No video'}</p></>
                        )}
                      </div>
                    )}
                  </div>
                  {(validationData.timeline.length > 0 || isAddingNewStep) && (
                    <UnifiedVideoTimeline
                      timeline={validationData.timeline}
                      actionSteps={validationData.action_steps}
                      duration={videoDuration || Number(validationData.video_meta.duration_hint_s) || 1}
                      activeStepId={selectedActionId}
                      currentTime={currentTime}
                      onSelectStep={setSelectedActionId}
                      onSeek={handleSeek}
                      onTrimChange={updateSelectedTimelineTrim}
                      newStepDraft={newStepDraft}
                      onNewStepTrimChange={updateNewStepDraftTrim}
                    />
                  )}
                </section>

                <aside className="hv-card hv-ontology-card">
                  <div className="hv-card-head">
                    <span className="hv-step-tag hv-step-tag-muted">Scene objects</span>
                  </div>
                  <p className="hv-card-desc">Global object list. Rename existing ontology here; add new focus objects in the current action step below.</p>
                  {validationData.objects.length > 0 ? (
                    <div className="validation-object-list">
                      {validationData.objects.map((item, index) => {
                        const isActive = isValidObjectName(item.name) && activeStepObjectNames.has(item.name.trim().toLowerCase());
                        return (
                          <div key={item._key || `object-${index}`} className={`validation-object-row ${isActive ? 'hv-object-row-active' : ''}`}>
                            <div className="hv-object-name-wrap">
                              <input type="text" className="form-control form-control-sm" value={item.name} placeholder="Name"
                                onFocus={() => handleObjectNameFocus(index)}
                                onChange={(e) => handleObjectNameInput(index, e.target.value)}
                                onBlur={() => handleObjectNameBlur(index)} />
                              {isActive && <span className="hv-object-active-badge">Active</span>}
                            </div>
                            <input type="number" className="form-control form-control-sm validation-count-input" min="0" value={item.count}
                              onChange={(e) => updateValidationData((prev) => ({
                                ...prev,
                                objects: prev.objects.map((o, i) => i === index ? { ...o, count: e.target.value === '' ? '' : toNumberOrEmpty(e.target.value) } : o)
                              }), { sanitize: false })} />
                            <button type="button" className="btn btn-light validation-icon-btn" onClick={() => handleRemoveOntologyObject(index)} aria-label="Remove">
                              <i className="bi bi-trash" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="validation-empty-state compact">
                      <p className="mb-0">No global objects yet. New action objects are added after reprocess.</p>
                    </div>
                  )}
                  <GlobalOntologyBar
                    renames={allGlobalRenames}
                    reprocessing={reprocessingOntology}
                    onApply={handleReprocessGlobalOntology}
                    disabled={!protocolId}
                  />
                </aside>
              </div>

              {isAddingNewStep ? (
                <NewActionStepPanel
                  draft={newStepDraft}
                  onTitleChange={(title) => setNewStepDraft((prev) => (prev ? { ...prev, title } : prev))}
                  onProcess={handleProcessNewActionStep}
                  onCancel={cancelNewActionStep}
                  processing={processingNewStep}
                  segmentStart={newStepDraft?.approx_start_s}
                  segmentEnd={newStepDraft?.approx_end_s}
                />
              ) : selectedActionStep && (
                <>
                  {/* Step 2: Human corrections */}
                  <section className="hv-card hv-corrections-card">
                    <div className="hv-card-head">
                      <span className="hv-step-tag hv-step-tag-human">Step 2 · Correct</span>
                    </div>
                    <p className="hv-card-desc">Fix what the system got wrong. Changes here are used when you reprocess — full visual reasoning is regenerated for this clip.</p>
                    <HumanChangesSummary baseline={baselineSelectedActionStep} edited={selectedActionStep} />
                    <div className="hv-corrections-grid">
                      <div className="hv-field">
                        <label className="form-label" htmlFor="hv-title">Action title</label>
                        <input id="hv-title" type="text" className="form-control hv-title-input" value={selectedActionStep.title}
                          onChange={(e) => updateSelectedActionStep((s) => ({ ...s, title: e.target.value }))}
                          onFocus={handleActionStepTitleFocus} onBlur={handleActionStepTitleBlur}
                          placeholder="e.g. Open the diary" />
                      </div>
                      <div className="hv-field hv-field-full">
                        <ActionStepObjectEditor
                          items={selectedActionStep.objects_expected}
                          onChange={(items) => updateSelectedActionStep(
                            (s) => ({ ...s, objects_expected: items }),
                            { sanitize: false }
                          )}
                          onItemFocus={handleActionStepObjectFocus}
                          onItemBlur={handleActionStepObjectBlur}
                          onRemoveItem={handleRemoveExpectedObject}
                          addLabel="Add object"
                          placeholder="Object for this action"
                        />
                      </div>
                      <div className="hv-field hv-field-full">
                        <label className="form-label" htmlFor="hv-context">Step context</label>
                        <textarea id="hv-context" className="form-control" rows="2" value={selectedActionStep.step_context}
                          onChange={(e) => updateSelectedActionStep((s) => ({ ...s, step_context: e.target.value }))}
                          placeholder="What happens visually in this clip" />
                      </div>
                    </div>
                  </section>

                  {/* Step 3: Reprocess — directly after corrections */}
                  <ReprocessActionBar
                    onReprocess={handleReprocess}
                    reprocessing={reprocessing}
                    disabled={!selectedActionStep}
                    segmentStart={selectedTimeline?.approx_start_s ?? null}
                    segmentEnd={selectedTimeline?.approx_end_s ?? null}
                    hasPendingOutput={Boolean(reprocessResult)}
                  />
                </>
              )}

              {/* Step 4: Review output */}
              {reprocessResult && (
                <EditableReprocessOutput
                  result={reprocessResult}
                  onChange={setReprocessResult}
                  onApprove={applyReprocessResult}
                  applying={applying}
                />
              )}

              {!isAddingNewStep && selectedActionStep && (
              <CollapsibleCard title="Advanced fields" subtitle="Hands, clip timing, criteria, prompts, metadata" defaultOpen={false} className="hv-advanced-card">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Left hand</label>
                    <input type="text" className="form-control" value={selectedActionStep.hand_pose_expectation.left}
                      onChange={(e) => updateSelectedActionStep((s) => ({ ...s, hand_pose_expectation: { ...s.hand_pose_expectation, left: e.target.value } }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Right hand</label>
                    <input type="text" className="form-control" value={selectedActionStep.hand_pose_expectation.right}
                      onChange={(e) => updateSelectedActionStep((s) => ({ ...s, hand_pose_expectation: { ...s.hand_pose_expectation, right: e.target.value } }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Clip start (s)</label>
                    <input type="number" className="form-control" min="0" step="0.1" value={selectedTimeline?.approx_start_s ?? ''}
                      onChange={(e) => updateSelectedTimeline('approx_start_s', e.target.value)} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Clip end (s)</label>
                    <input type="number" className="form-control" min="0" step="0.1" value={selectedTimeline?.approx_end_s ?? ''}
                      onChange={(e) => updateSelectedTimeline('approx_end_s', e.target.value)} />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Reference prompt</label>
                    <textarea className="form-control" rows="3" value={selectedPrompt?.prompt || ''} onChange={(e) => updateSelectedPrompt(e.target.value)} />
                  </div>
                  <div className="col-12">
                    <ListEditor label="Detailed steps" items={selectedActionStep.detailed_steps}
                      onChange={(items) => updateSelectedActionStep((s) => ({ ...s, detailed_steps: items }))} addLabel="Add" placeholder="Micro-step" />
                  </div>
                  <div className="col-12">
                    <ListEditor label="Success criteria" items={selectedActionStep.success_criteria}
                      onChange={(items) => updateSelectedActionStep((s) => ({ ...s, success_criteria: items }))} addLabel="Add" placeholder="Completion rule" />
                  </div>
                  <div className="col-12">
                    <ListEditor label="Failure modes" items={selectedActionStep.failure_modes}
                      onChange={(items) => updateSelectedActionStep((s) => ({ ...s, failure_modes: items }))} addLabel="Add" placeholder="Violation" />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Global context</label>
                    <textarea className="form-control" rows="2" value={validationData.reference_prompts.global_context}
                      onChange={(e) => updateValidationData((prev) => ({ ...prev, reference_prompts: { ...prev.reference_prompts, global_context: e.target.value } }))} />
                  </div>
                </div>
              </CollapsibleCard>
              )}
            </>
          )}
        </div>
      </div>
    </QuratorLayout>
  );
};

export default HumanValidation;
